/* 911-fix-iter172 — Billing Diagnostics page.
   Dan asked for a way to find invoices Adam submitted that aren't showing
   up in All Invoices. This page surfaces:

     1. Decisions where the tech picked "Create Invoice Now" (or similar) but
        the invoice was never created.
     2. Recent completions with their linked invoice (if any).
     3. Recent invoices created via the Completion Wizard.
     4. "Orphan" invoices missing `source_appointment_id` (these don't link
        back to the shift in Billing Missing). A one-click backfill button
        copies appointment_id → source_appointment_id on every such doc.
*/
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Link, useSearchParams } from 'react-router-dom';
import { AlertOctagon, Wrench, Search, ExternalLink, Hammer, Users, Inbox, ShieldAlert, CalendarClock } from 'lucide-react';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import BillingByTechTab from '../../components/BillingByTechTab';
import BillingInboxTab from '../../components/BillingInboxTab';
import BillingIntegrityTab from '../../components/BillingIntegrityTab';
import WeeklyBillingFollowUpTab from '../../components/WeeklyBillingFollowUpTab';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDt = (d) => {
  if (!d) return '';
  try { return new Date(d).toLocaleString(); } catch { return String(d); }
};

const BillingDiagnostics = () => {
  const [days, setDays] = useState(14);
  const [techId, setTechId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [backfilling, setBackfilling] = useState(false);
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = searchParams.get('tab') || 'followup';

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('days', String(days));
      if (techId.trim()) params.set('technician_id', techId.trim());
      const { data: d } = await axios.get(`${API}/admin/billing-diagnostics?${params}`, { withCredentials: true });
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to load diagnostics');
    }
    setLoading(false);
  };

  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const runBackfill = async () => {
    setBackfilling(true);
    try {
      const { data: d } = await axios.post(`${API}/admin/billing-diagnostics/backfill-source-appointment`, {}, { withCredentials: true });
      toast.success(`Backfilled ${d.modified} of ${d.matched} invoices`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Backfill failed');
    }
    setBackfilling(false);
  };

  return (
    <div data-testid="billing-diagnostics" className="space-y-6">
      <div>
        <h2 className="font-['Outfit'] text-3xl font-bold text-white mb-1 flex items-center gap-2">
          <AlertOctagon className="w-6 h-6 text-red-400" /> Billing Diagnostics
        </h2>
        <p className="text-[#A3A3A3] max-w-3xl text-sm">
          Find invoices that didn&apos;t make it to All Invoices. The first bucket shows decisions where a tech picked
          &quot;Create Invoice Now&quot; (or similar) but no invoice doc was ever created — those are the cases that silently
          drop off Billing Missing. The last bucket lists legacy invoices missing <code>source_appointment_id</code>;
          run Backfill to instantly reconnect them to their shifts.
        </p>
      </div>

      {/* Controls */}
      <div className="bg-[#141414] border border-[#262626] rounded-md p-4 flex flex-wrap items-end gap-3">
        <div>
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Window (days)</Label>
          <Input
            data-testid="diag-days"
            type="number"
            min="1"
            max="90"
            value={days}
            onChange={(e) => setDays(Number(e.target.value || 14))}
            className="w-24 bg-[#0A0A0A] border-[#262626] text-white mt-1"
          />
        </div>
        <div className="flex-1 min-w-[180px]">
          <Label className="text-xs text-[#A3A3A3] uppercase tracking-wider">Technician ID (optional)</Label>
          <Input
            data-testid="diag-tech-id"
            value={techId}
            onChange={(e) => setTechId(e.target.value)}
            placeholder="paste user id to filter to one tech"
            className="bg-[#0A0A0A] border-[#262626] text-white mt-1"
          />
        </div>
        <Button
          data-testid="diag-refresh"
          onClick={load}
          disabled={loading}
          className="bg-[#D4AF37] text-[#0A0A0A] hover:bg-[#F3E5AB] font-bold"
        >
          <Search className="w-4 h-4 mr-1" /> {loading ? 'Loading…' : 'Refresh'}
        </Button>
        <Button
          data-testid="diag-backfill"
          onClick={runBackfill}
          disabled={backfilling}
          variant="outline"
          className="border-red-500/40 text-red-300 hover:bg-red-500/10"
          title="One-shot: copies appointment_id → source_appointment_id on all invoices missing it."
        >
          <Hammer className="w-4 h-4 mr-1" /> {backfilling ? 'Backfilling…' : 'Backfill source_appointment_id'}
        </Button>
      </div>

      <Tabs value={tab} onValueChange={(v) => setSearchParams({ tab: v }, { replace: true })} className="w-full">
        <TabsList data-testid="billing-diag-tabs" className="bg-[#141414] border border-[#262626]">
          <TabsTrigger data-testid="billing-diag-tab-followup" value="followup" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] data-[state=active]:font-bold">
            <CalendarClock className="w-4 h-4 mr-1.5" /> Weekly Follow-Up
          </TabsTrigger>
          <TabsTrigger data-testid="billing-diag-tab-inbox" value="inbox" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] data-[state=active]:font-bold">
            <Inbox className="w-4 h-4 mr-1.5" /> Billing Inbox
          </TabsTrigger>
          <TabsTrigger data-testid="billing-diag-tab-integrity" value="integrity" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] data-[state=active]:font-bold">
            <ShieldAlert className="w-4 h-4 mr-1.5" /> Integrity Scan
          </TabsTrigger>
          <TabsTrigger data-testid="billing-diag-tab-decisions" value="decisions" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] data-[state=active]:font-bold">
            <AlertOctagon className="w-4 h-4 mr-1.5" /> Decisions
          </TabsTrigger>
          <TabsTrigger data-testid="billing-diag-tab-bytech" value="bytech" className="text-[#A3A3A3] data-[state=active]:bg-[#D4AF37] data-[state=active]:text-[#0A0A0A] data-[state=active]:font-bold">
            <Users className="w-4 h-4 mr-1.5" /> By Tech
          </TabsTrigger>
        </TabsList>

        <TabsContent value="followup" className="mt-5"><WeeklyBillingFollowUpTab /></TabsContent>
        <TabsContent value="inbox" className="mt-5"><BillingInboxTab /></TabsContent>
        <TabsContent value="integrity" className="mt-5"><BillingIntegrityTab /></TabsContent>

        <TabsContent value="decisions" className="mt-5">

      {!data && loading && <p className="text-[#A3A3A3]">Loading diagnostics…</p>}

      {data && (
        <>
          {/* Summary tiles */}
          <div className="grid sm:grid-cols-4 gap-3">
            <Tile testid="diag-tile-decided" label="Decisions w/o Invoice" value={data.summary.decided_no_invoice_count} accent="text-red-400" />
            <Tile testid="diag-tile-completions" label="Recent Completions" value={data.summary.recent_completion_count} accent="text-white" />
            <Tile testid="diag-tile-invoices" label="Recent Invoices" value={data.summary.recent_invoice_count} accent="text-[#D4AF37]" />
            <Tile testid="diag-tile-orphans" label="Orphan Invoices" value={data.summary.orphan_invoice_count} accent="text-amber-300" />
          </div>

          {/* Decided no invoice */}
          <Section
            testid="diag-decided-no-invoice"
            title="Decisions picked but no invoice created"
            subtitle="The tech committed an invoice-requiring decision (Create Invoice Now / Save Draft / HSP / Deferred / Membership Included) but the invoice POST never landed. These shifts still belong in Billing Missing — open the linked appointment from /admin/schedule to finish the closeout."
            count={data.decided_no_invoice.length}
            rows={data.decided_no_invoice}
            empty="Nothing missing — every recent decision has an invoice."
            renderRow={(r) => (
              <>
                <span className="text-white font-bold">{r.customer_name || '—'}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-white">{r.submitted_by_name || '?'}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-red-300 uppercase text-xs font-bold">{r.invoice_status}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-[#A3A3A3]">{fmtDt(r.invoice_status_set_at)}</span>
                {r.appointment_id && (
                  <Link
                    to={`/admin/schedule?open_appt=${r.appointment_id}`}
                    className="ml-auto inline-flex items-center gap-1 text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-bold"
                  >
                    <ExternalLink className="w-3 h-3" /> Open Shift
                  </Link>
                )}
              </>
            )}
          />

          {/* Recent completions */}
          <Section
            testid="diag-recent-completions"
            title="Recent Completions"
            subtitle="Every completion submitted in the window, with its linked invoice (if any)."
            count={data.recent_completions.length}
            rows={data.recent_completions}
            renderRow={(r) => (
              <>
                <span className="text-white font-bold">{r.customer_name || '—'}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-white">{r.submitted_by_name || '?'}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-[#A3A3A3] uppercase text-xs">{r.invoice_status || '—'}</span>
                <span className="text-[#A3A3A3]">·</span>
                {r.linked_invoice ? (
                  <Link
                    to={`/admin/invoices?status=all&open=${r.linked_invoice.id}`}
                    className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-bold"
                  >
                    {r.linked_invoice.invoice_number || `inv ${r.linked_invoice.id.slice(-6)}`} · ${(r.linked_invoice.amount || 0).toFixed(2)}
                  </Link>
                ) : (
                  <span className="text-red-300 text-xs font-bold">NO INVOICE</span>
                )}
              </>
            )}
          />

          {/* Recent invoices */}
          <Section
            testid="diag-recent-invoices"
            title="Recent Invoices"
            subtitle="Every invoice created in the window — confirms tech submissions actually persisted."
            count={data.recent_invoices.length}
            rows={data.recent_invoices}
            renderRow={(r) => (
              <>
                <Link
                  to={`/admin/invoices?status=all&open=${r.id}`}
                  className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-bold"
                >
                  {r.invoice_number || r.id.slice(-6)}
                </Link>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-white">{r.customer_name || '—'}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-white">${(r.amount || 0).toFixed(2)}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-[#A3A3A3] uppercase text-xs">{r.status}</span>
                {r.source && <><span className="text-[#A3A3A3]">·</span><span className="text-[#737373] text-xs">{r.source}</span></>}
              </>
            )}
          />

          {/* Orphan invoices */}
          <Section
            testid="diag-orphan-invoices"
            title="Orphan Invoices (missing source_appointment_id)"
            subtitle="Invoices that have an appointment_id but no source_appointment_id. They render in All Invoices but don't clear their shift from Billing Missing. Use the Backfill button above to migrate them — runs in a single Mongo update_many."
            count={data.orphan_invoices.length}
            rows={data.orphan_invoices}
            empty="No orphans — every invoice is properly linked."
            renderRow={(r) => (
              <>
                <Link
                  to={`/admin/invoices?status=all&open=${r.id}`}
                  className="text-[#D4AF37] hover:text-[#F3E5AB] text-xs font-bold"
                >
                  {r.invoice_number || r.id.slice(-6)}
                </Link>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-white">{r.customer_name || '—'}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-white">${(r.amount || 0).toFixed(2)}</span>
                <span className="text-[#A3A3A3]">·</span>
                <span className="text-amber-300 text-xs">appt: {(r.appointment_id || '').slice(-6)}</span>
              </>
            )}
          />
        </>
      )}
        </TabsContent>

        <TabsContent value="bytech" className="mt-5">
          <BillingByTechTab />
        </TabsContent>
      </Tabs>
    </div>
  );
};

const Tile = ({ testid, label, value, accent }) => (
  <div data-testid={testid} className="bg-[#141414] border border-[#262626] rounded-md p-4">
    <p className="text-[#525252] text-xs uppercase tracking-wide">{label}</p>
    <p className={`mt-2 text-3xl font-bold ${accent}`}>{value}</p>
  </div>
);

const Section = ({ testid, title, subtitle, count, rows, renderRow, empty }) => (
  <div data-testid={testid} className="bg-[#141414] border border-[#262626] rounded-md p-4">
    <div className="flex items-center justify-between gap-2 flex-wrap">
      <div>
        <h3 className="font-['Outfit'] text-lg font-semibold text-white flex items-center gap-2">
          <Wrench className="w-4 h-4 text-[#D4AF37]" /> {title}
          <span className="text-[#A3A3A3] text-sm font-normal">· {count}</span>
        </h3>
        <p className="text-[#737373] text-xs mt-1 max-w-3xl">{subtitle}</p>
      </div>
    </div>
    {rows.length === 0 ? (
      <p className="text-[#525252] text-sm italic mt-3">{empty || 'No rows in this window.'}</p>
    ) : (
      <div className="mt-3 space-y-1.5">
        {rows.map((r) => (
          <div
            key={r.completion_id || r.id || JSON.stringify(r).slice(0, 32)}
            className="bg-[#0A0A0A] border border-[#262626] rounded px-2.5 py-1.5 flex items-center gap-2 text-sm flex-wrap"
          >
            {renderRow(r)}
          </div>
        ))}
      </div>
    )}
  </div>
);

export default BillingDiagnostics;
